import React, { useRef, useState, useCallback, memo } from "react";
import {
  Box, Typography, TextField, Grid, Button, LinearProgress,
  FormControlLabel, Checkbox, FormLabel, IconButton,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateField } from "@mui/x-date-pickers/DateField";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from "../components/utils/api";
import { uuidv4 } from "../components/Id/uuidv4";
import FileUploader from "../components/FileUploader/FileUploader";
import Loading from "../components/Loading/Loading";
import { playSuccess, playError } from "../components/utils/sounds";
import "./stylefirma.css";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

const ORANGE = "#ED9519";

// ─── Pasos del wizard ─────────────────────────────────────────────────────────
const STEPS = [
  { title: "Datos del cliente",     icon: PersonOutlineIcon,        fields: ["cliente","contacto","direccion","localidad","provincia"] },
  { title: "Datos de la máquina",   icon: BuildOutlinedIcon,        fields: ["modelo","chasis","motor","horometro","flagReparacion","flagServicio","flagGarantia","otros"] },
  { title: "Descripción",           icon: DescriptionOutlinedIcon,  fields: ["falla","servicioRealizado"] },
  { title: "Repuestos",             icon: InventoryOutlinedIcon,    fields: [] },
  { title: "Datos del técnico",     icon: EngineeringOutlinedIcon,  fields: ["km"] },
  { title: "Firma del cliente",     icon: DrawOutlinedIcon,         fields: ["canvas","aclaracionFirma","dni"] },
];

// ─── Schema de validación ─────────────────────────────────────────────────────
const req = "El campo es requerido";
const reqFlag = "Seleccioná al menos un tipo o completá 'Otros'";

const validationSchema = Yup.object({
  cliente:          Yup.string().required(req),
  contacto:         Yup.string().required(req),
  direccion:        Yup.string().required(req).matches(/^(?=.*[a-zA-Z\s])(?=.*\d).*$/, "Debe contener letras y números"),
  localidad:        Yup.string().required(req),
  provincia:        Yup.string().required(req),
  modelo:           Yup.string().required(req),
  chasis:           Yup.string().required(req),
  motor:            Yup.string().required(req),
  horometro:        Yup.string().required(req),
  falla:            Yup.string().required(req),
  servicioRealizado:Yup.string().required(req),
  km:               Yup.number().required(req),
  canvas:           Yup.string().required(req),
  aclaracionFirma:  Yup.string().required(req),
  dni:              Yup.number().required(req),
  flagReparacion: Yup.boolean().test("flag", reqFlag, function(v) {
    const { otros, flagServicio, flagGarantia } = this.parent;
    return (otros || flagServicio || flagGarantia) ? true : v || this.createError({ message: reqFlag });
  }),
  flagServicio: Yup.boolean().test("flag", reqFlag, function(v) {
    const { otros, flagReparacion, flagGarantia } = this.parent;
    return (otros || flagReparacion || flagGarantia) ? true : v || this.createError({ message: reqFlag });
  }),
  flagGarantia: Yup.boolean().test("flag", reqFlag, function(v) {
    const { otros, flagReparacion, flagServicio } = this.parent;
    return (otros || flagReparacion || flagServicio) ? true : v || this.createError({ message: reqFlag });
  }),
  otros: Yup.string().test("flag", reqFlag, function(v) {
    const { flagReparacion, flagServicio, flagGarantia } = this.parent;
    return (flagReparacion || flagServicio || flagGarantia) ? true : v || this.createError({ message: reqFlag });
  }),
});

// ─── Pantalla de éxito ───────────────────────────────────────────────────────
const CONFETTI = ["#ED9519", "#4ade80", "#60a5fa", "#a78bfa", "#f472b6", "#ED9519"];

const SuccessScreen = memo(({ serviceId, offline, onContinue }) => {
  // Sin timer — aparece directamente. La animación es solo CSS.
  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 56px)",
      bgcolor: "background.default",
      alignItems: "center", justifyContent: "center",
      px: 3, gap: 2.5,
      "@keyframes fadeUp": {
        from: { opacity: 0, transform: "translateY(24px)" },
        to:   { opacity: 1, transform: "translateY(0)" },
      },
      animation: "fadeUp 0.4s ease forwards",
    }}>

      {/* Confetti */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 0.5 }}>
        {CONFETTI.map((color, i) => (
          <Box key={i} sx={{
            width: 8, height: 8, borderRadius: "50%", bgcolor: color,
            "@keyframes bounce": {
              "0%,100%": { transform: "translateY(0)" },
              "50%":     { transform: "translateY(-10px)" },
            },
            animation: `bounce 1.2s ease-in-out ${i * 0.12}s infinite`,
          }} />
        ))}
      </Box>

      {/* Círculo con ícono */}
      <Box sx={{
        width: 90, height: 90, borderRadius: "50%",
        bgcolor: offline ? "rgba(96,165,250,0.1)" : "rgba(74,222,128,0.1)",
        border: "2px solid",
        borderColor: offline ? "rgba(96,165,250,0.35)" : "rgba(74,222,128,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        "@keyframes pop": {
          "0%":   { transform: "scale(0.6)" },
          "70%":  { transform: "scale(1.12)" },
          "100%": { transform: "scale(1)" },
        },
        animation: "pop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.15s both",
      }}>
        {offline
          ? <CloudDoneOutlinedIcon sx={{ fontSize: 44, color: "#60a5fa" }} />
          : <CheckCircleOutlineIcon sx={{ fontSize: 44, color: "#4ade80" }} />}
      </Box>

      {/* Texto principal */}
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
          {offline ? "¡Guardado localmente!" : "¡Servicio creado!"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, maxWidth: 280 }}>
          {offline
            ? "Los datos se guardaron en tu dispositivo. Cuando tengas conexión, enviálos desde Servicios Pendientes."
            : "El servicio fue registrado correctamente en el sistema."}
        </Typography>
      </Box>

      {/* Badge ID o badge offline */}
      {serviceId && !offline && (
        <Box sx={{
          bgcolor: "rgba(237,149,25,0.08)", border: "1px solid rgba(237,149,25,0.2)",
          borderRadius: 2, px: 2.5, py: 1,
        }}>
          <Typography sx={{ fontSize: 13, color: "#ED9519", fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.06em" }}>
            # Servicio {String(serviceId).padStart(5, "0")}
          </Typography>
        </Box>
      )}

      {offline && (
        <Box sx={{
          bgcolor: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.2)",
          borderRadius: 2, px: 2.5, py: 1,
        }}>
          <Typography sx={{ fontSize: 12, color: "#60a5fa", fontWeight: 500 }}>
            Pendiente de envío · Sin conexión
          </Typography>
        </Box>
      )}

      {/* Botón */}
      <Button
        fullWidth
        variant="outlined"
        endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
        onClick={onContinue}
        sx={{ mt: 1, maxWidth: 320, borderRadius: 2.5, py: 1.2, fontSize: 14, fontWeight: 600 }}
      >
        {offline ? "Ver servicios pendientes" : "Volver a servicios"}
      </Button>
    </Box>
  );
});

// ─── Header con navegación integrada ─────────────────────────────────────────
const StepHeader = memo(({ step, total, title, StepIcon, onBack, onNext, isLastStep, loading }) => (
  <Box sx={{ bgcolor: "#ED9519", flexShrink: 0 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}>

      {/* Botón Anterior */}
      <IconButton
        onClick={onBack}
        disabled={step === 0}
        size="small"
        sx={{
          width: 36, height: 36, borderRadius: 2, flexShrink: 0,
          bgcolor: step === 0 ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.18)",
          color: "#1a0e00",
          "&:hover": { bgcolor: "rgba(0,0,0,0.25)" },
          "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.05)", color: "rgba(26,14,0,0.25)" },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Centro: ícono + título + paso */}
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <StepIcon sx={{ fontSize: 14, color: "#1a0e00", opacity: 0.7 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a0e00", lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 10, color: "#1a0e00", opacity: 0.55 }}>
          Paso {step + 1} de {total}
        </Typography>
      </Box>

      {/* Botón Siguiente / Enviar */}
      <IconButton
        onClick={onNext}
        disabled={loading}
        size="small"
        sx={{
          width: 36, height: 36, borderRadius: 2, flexShrink: 0,
          bgcolor: isLastStep ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.18)",
          color: "#1a0e00",
          "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
          "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.1)", color: "rgba(26,14,0,0.3)" },
        }}
      >
        {isLastStep
          ? <SendIcon sx={{ fontSize: 17 }} />
          : <ArrowForwardIcon sx={{ fontSize: 18 }} />}
      </IconButton>

    </Box>

    {/* Barra de progreso */}
    <LinearProgress
      variant="determinate"
      value={((step + 1) / total) * 100}
      sx={{
        height: 3,
        bgcolor: "rgba(0,0,0,0.15)",
        "& .MuiLinearProgress-bar": { bgcolor: "rgba(0,0,0,0.45)", borderRadius: 0 },
      }}
    />
  </Box>
));

// ─── Componente principal ─────────────────────────────────────────────────────
export default function NuevoServicio() {
  const userToken = localStorage.getItem("user-token");
  const user = localStorage.getItem("nombre-Completo");
  const navigate = useNavigate();
  const padRef = useRef(null);

  const [step, setStep] = useState(0);
  const [fecha, setFecha] = useState(dayjs().tz(dayjs.tz.guess()));
  const [horaInicio, setHoraInicio] = useState(dayjs());
  const [horaFin, setHoraFin] = useState(dayjs());
  const [imgServicio, setImgServicio] = useState([]);
  const [loadFile, setLoadFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([{ id: 1 }]);
  const [successData, setSuccessData] = useState(null); // { id, offline }

  const formik = useFormik({
    initialValues: {
      cliente:"", contacto:"", direccion:"", localidad:"", provincia:"",
      modelo:"", chasis:"", motor:"", horometro:"", hodometro:"",
      falla:"", servicioRealizado:"", km:"", canvas:"", aclaracionFirma:"",
      flagReparacion:false, flagServicio:false, flagGarantia:false, otros:"",
      tecnico: user, dni:"", idInterno: uuidv4(),
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => enviarFormulario(values),
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;

  // ─── Navegación entre pasos ────────────────────────────────────────────────
  const goNext = useCallback(async () => {
    const currentFields = STEPS[step].fields;
    if (currentFields.length === 0) { setStep(s => s + 1); return; }

    const allErrors = await formik.validateForm();
    const newTouched = currentFields.reduce((acc, f) => ({ ...acc, [f]: true }), {});
    formik.setTouched({ ...formik.touched, ...newTouched }, false);

    const hasError = currentFields.some(f => allErrors[f]);
    if (hasError) return;

    setStep(s => s + 1);
  }, [step, formik]);

  const goBack = useCallback(() => setStep(s => s - 1), []);

  // ─── Firma ────────────────────────────────────────────────────────────────
  const clearCanvas = useCallback(() => {
    padRef?.current?.clear();
    setFieldValue("canvas", "");
  }, [setFieldValue]);

  const saveCanvas = useCallback(() => {
    setFieldValue("canvas", padRef?.current?.toDataURL());
  }, [setFieldValue]);

  // ─── Repuestos ────────────────────────────────────────────────────────────
  const handleLineChange = useCallback((e, id, field) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: e.target.value } : l));
  }, []);
  const addLine = useCallback(() => setLines(p => [...p, { id: p.length + 1 }]), []);
  const delLine = useCallback(() => setLines(p => p.length > 1 ? p.slice(0, -1) : p), []);
  const buildRepuestos = useCallback(() => lines.map(({ id, ...r }) => r), [lines]);

  // ─── Envío final ──────────────────────────────────────────────────────────
  const handleSubmitFinal = useCallback(async () => {
    if (loadFile) { swal("Revisa el formulario", "Tenés archivos pendientes de cargar", "error"); return; }
    if (!values.canvas) { swal("Revisa el formulario", "La firma no fue guardada", "error"); return; }
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) { formik.handleSubmit(); return; }
    formik.handleSubmit();
  }, [loadFile, values.canvas, formik]);

  const enviarFormulario = async (values) => {
    try {
      const ok = await swal({ title: "¿Estás seguro?", text: "Verificá los datos antes de enviar.", icon: "warning", buttons: true, dangerMode: true });
      if (!ok) return;

      const body = {
        cliente: values.cliente, fecha, contacto: values.contacto, direccion: values.direccion,
        localidad: values.localidad, dni: values.dni, provincia: values.provincia, modelo: values.modelo,
        chasis: values.chasis, motor: values.motor, tipoServicioReparacion: values.flagReparacion,
        tipoServicioServicio: values.flagServicio, tipoServicioGarantia: values.flagGarantia,
        tipoServicioOtros: values.otros, descripcionFalla: values.falla, servicio: values.servicioRealizado,
        kmRecorridos: values.km, horaInicio, horaFin, tecnico: values.tecnico,
        firmaTecnico: localStorage.getItem("firma"), firmaCliente: values.canvas,
        aclaracionCliente: values.aclaracionFirma, horometro: values.horometro,
        hodometro: values.hodometro, flagCrm: "0", idInterno: values.idInterno,
        repuestosPorServicios: buildRepuestos(), imagenesPorServicios: imgServicio,
      };
      const opts = { method: "POST", body: JSON.stringify(body),
        headers: { "Content-type": "application/json; charset=UTF-8", "iron-token": userToken } };

      if (navigator.onLine) {
        setLoading(true);
        const res = await fetch(api.urlAPI + "servicios", opts);
        const data = await res.json();
        setLoading(false);
        playSuccess();
        setSuccessData({ id: data.data?.id || null, offline: false });
      } else {
        const offOk = await swal({ title: "Sin conexión", text: "Se guardará localmente. Envialo desde Servicios Pendientes cuando tengas conexión.", icon: "warning", buttons: ["Cancelar", "Guardar"], dangerMode: false });
        if (!offOk) return;
        const posts = JSON.parse(localStorage.getItem("offlinePosts")) || [];
        posts.push(opts);
        localStorage.setItem("offlinePosts", JSON.stringify(posts));
        if ("serviceWorker" in navigator && "SyncManager" in window)
          navigator.serviceWorker.ready.then(r => r.sync.register("syncPosts")).catch(() => {});
        playSuccess();
        setSuccessData({ id: null, offline: true });
      }
    } catch {
      playError();
      swal({ title: "Error al enviar", text: "Contactá al administrador.", icon: "error", timer: 3000 });
    } finally { setLoading(false); }
  };

  const StepIcon = STEPS[step].icon;

  // ─── Render de cada paso ──────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0: return (
        <Grid container spacing={1.5}>
          <Grid item xs={8}>
            <TextField fullWidth required size="small" id="cliente" label="Cliente"
              value={values.cliente} onChange={handleChange} onBlur={handleBlur}
              error={touched.cliente && Boolean(errors.cliente)} helperText={touched.cliente && errors.cliente} />
          </Grid>
          <Grid item xs={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateField size="small" label="Fecha" value={fecha} onChange={setFecha} fullWidth required />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" id="contacto" label="Contacto"
              value={values.contacto} onChange={handleChange} onBlur={handleBlur}
              error={touched.contacto && Boolean(errors.contacto)} helperText={touched.contacto && errors.contacto} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" id="direccion" label="Dirección"
              value={values.direccion} onChange={handleChange} onBlur={handleBlur}
              error={touched.direccion && Boolean(errors.direccion)} helperText={touched.direccion && errors.direccion} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" id="localidad" label="Localidad"
              value={values.localidad} onChange={handleChange} onBlur={handleBlur}
              error={touched.localidad && Boolean(errors.localidad)} helperText={touched.localidad && errors.localidad} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" id="provincia" label="Provincia"
              value={values.provincia} onChange={handleChange} onBlur={handleBlur}
              error={touched.provincia && Boolean(errors.provincia)} helperText={touched.provincia && errors.provincia} />
          </Grid>
        </Grid>
      );

      case 1: return (
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required size="small" id="modelo" label="Modelo"
              value={values.modelo} onChange={handleChange} onBlur={handleBlur}
              error={touched.modelo && Boolean(errors.modelo)} helperText={touched.modelo && errors.modelo} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" id="chasis" label="N° Chasis"
              value={values.chasis} onChange={handleChange} onBlur={handleBlur}
              error={touched.chasis && Boolean(errors.chasis)} helperText={touched.chasis && errors.chasis} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" id="motor" label="N° Motor"
              value={values.motor} onChange={handleChange} onBlur={handleBlur}
              error={touched.motor && Boolean(errors.motor)} helperText={touched.motor && errors.motor} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" id="horometro" label="Horómetro"
              value={values.horometro} onChange={handleChange} onBlur={handleBlur}
              error={touched.horometro && Boolean(errors.horometro)} helperText={touched.horometro && errors.horometro} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" id="hodometro" label="Hodómetro"
              value={values.hodometro} onChange={handleChange} onBlur={handleBlur} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>Tipo de servicio *</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {[
                { id: "flagReparacion", label: "Reparación", disabled: values.otros !== "" || values.flagServicio || values.flagGarantia },
                { id: "flagServicio",   label: "Servicio",   disabled: values.otros !== "" || values.flagReparacion || values.flagGarantia },
                { id: "flagGarantia",   label: "Garantía",   disabled: values.otros !== "" || values.flagServicio || values.flagReparacion },
              ].map(({ id, label, disabled }) => (
                <FormControlLabel key={id} label={<Typography variant="body2">{label}</Typography>}
                  control={<Checkbox size="small" checked={values[id]} disabled={disabled}
                    onChange={e => setFieldValue(id, e.target.checked)} />} />
              ))}
            </Box>
            {(touched.flagReparacion && errors.flagReparacion) && (
              <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>{errors.flagReparacion}</Typography>
            )}
            <TextField fullWidth size="small" id="otros" label="Otros (si no aplica ninguno de los anteriores)"
              sx={{ mt: 1 }} value={values.otros} onChange={handleChange} onBlur={handleBlur}
              disabled={values.flagReparacion || values.flagServicio || values.flagGarantia}
              error={touched.otros && Boolean(errors.otros)} helperText={touched.otros && errors.otros} />
          </Grid>
        </Grid>
      );

      case 2: return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField fullWidth required size="small" id="falla" label="Descripción de la falla" multiline minRows={2} maxRows={4}
              value={values.falla} onChange={handleChange} onBlur={handleBlur}
              error={touched.falla && Boolean(errors.falla)} helperText={touched.falla && errors.falla} />
          </Grid>
          <Grid item xs={12}>
            <FileUploader setImgServicio={setImgServicio} imgServicio={imgServicio}
              setLoadFile={setLoadFile} idInterno={values.idInterno} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required size="small" id="servicioRealizado" label="Servicio realizado" multiline minRows={2} maxRows={4}
              value={values.servicioRealizado} onChange={handleChange} onBlur={handleBlur}
              error={touched.servicioRealizado && Boolean(errors.servicioRealizado)}
              helperText={touched.servicioRealizado && errors.servicioRealizado} />
          </Grid>
        </Grid>
      );

      case 3: return (
        <Box>
          {lines.map(line => (
            <Grid container spacing={1} key={line.id} sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Cant." type="number"
                  value={line.cantidad || ""} onChange={e => handleLineChange(e, line.id, "cantidad")} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Código"
                  value={line.codigo || ""} onChange={e => handleLineChange(e, line.id, "codigo")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Descripción"
                  value={line.descripcion || ""} onChange={e => handleLineChange(e, line.id, "descripcion")} />
              </Grid>
            </Grid>
          ))}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button size="small" variant="outlined" onClick={addLine} sx={{ flex: 1 }}>+ Agregar línea</Button>
            <Button size="small" variant="outlined" color="warning" onClick={delLine} sx={{ flex: 1 }}
              disabled={lines.length <= 1}>− Eliminar línea</Button>
          </Box>
        </Box>
      );

      case 4: return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Técnico interviniente" value={values.tecnico} disabled />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required size="small" id="km" label="Km. recorridos" type="number"
              value={values.km} onChange={handleChange} onBlur={handleBlur}
              error={touched.km && Boolean(errors.km)} helperText={touched.km && errors.km} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Hora inicio" value={horaInicio} onChange={setHoraInicio} ampm={false}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Hora fin" value={horaFin} onChange={setHoraFin} ampm={false}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
            </LocalizationProvider>
          </Grid>
        </Grid>
      );

      case 5: return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5 }}>Firma del cliente</Typography>
            <Box sx={{ border: `1px solid rgba(255,255,255,0.15)`, borderRadius: 2, overflow: "hidden", bgcolor: "#fff", width: 280, height: 160 }}>
              <SignatureCanvas ref={padRef} penColor="#111318"
                canvasProps={{ width: 280, height: 160, style: { display: "block" } }} />
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" onClick={clearCanvas}>Limpiar</Button>
              <Button size="small" variant="contained" onClick={saveCanvas}>Guardar firma</Button>
            </Box>
            {touched.canvas && errors.canvas && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors.canvas}</Typography>
            )}
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" id="dni" label="DNI cliente"
                value={values.dni} onChange={handleChange} onBlur={handleBlur}
                error={touched.dni && Boolean(errors.dni)} helperText={touched.dni && errors.dni} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" id="aclaracionFirma" label="Aclaración firma"
                value={values.aclaracionFirma} onChange={handleChange} onBlur={handleBlur}
                error={touched.aclaracionFirma && Boolean(errors.aclaracionFirma)}
                helperText={touched.aclaracionFirma && errors.aclaracionFirma} />
            </Grid>
          </Grid>
        </Box>
      );

      default: return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;

  // ── Pantalla de éxito ──────────────────────────────────────────────────────
  if (successData) {
    return (
      <SuccessScreen
        serviceId={successData.id}
        offline={successData.offline}
        onContinue={() => navigate(successData.offline ? "/Servicios-Pendientes" : "/Servicios")}
      />
    );
  }

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 56px)",
      bgcolor: "background.default",
    }}>
      <StepHeader
        step={step}
        total={STEPS.length}
        title={STEPS[step].title}
        StepIcon={StepIcon}
        onBack={goBack}
        onNext={isLastStep ? handleSubmitFinal : goNext}
        isLastStep={isLastStep}
        loading={loading}
      />

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        {renderStep()}
      </Box>

      {loading && <Loading />}
    </Box>
  );
}
